import { useState, useEffect } from "react";
import shallow from "zustand/shallow";
import { clsx } from "clsx";
import CustomModal from "../CustomModal/CustomModal";
import { useStoreRoom, useStoreSystem } from "../../../store";
import styles from "./EditDecorModal.module.scss";
import calcRelativePaddings from "../../../store/helpers/calcRelativePaddings";
// import calcAvailablePaddings from "../../../store/helpers/calcAvailablePaddings";
import { SizeBlock } from "../../SizeBlock/SizeBlock";
import {
    DepthSize,
    LinearSize,
    LocationOfObject,
    MarginBoxSize,
    MarginUp,
    ObjectSize,
    ResetForButton,
    SizeFromLeft,
    Trashcan,
    VerticalSize,
} from "../../SVGasComponent";
import { MarginForward } from "../../SVGasComponent/MarginForward";
import { DecorItemI } from "../../../shared";
import { degToRad, radToDeg } from "three/src/math/MathUtils";
import { RotationOfObject } from "../../SVGasComponent/RotationOfObject";
import { RotateXAxis } from "../../SVGasComponent/RotateXAxis";
import { RotateYAxis } from "../../SVGasComponent/RotateYAxis";
import { RotateZAxis } from "../../SVGasComponent/RotateZAxis";
import { Vector3 } from "three";
import { calculateStretchToWallsAdvanced } from "../../../utils/stretchInnerWallsToOuterWalls";
import { createTriangularPrism, resetTriangularPrism, isTriangularPrism, cycleTriangularDirection } from "../../../utils/createTriangularPrism";

interface PropertyInterface {
    width: number;
    height: number;
    depth: number;
    margin: number;
    paddingLeft: number;
    paddingRight: number;
    paddingTop: number;
    paddingBottom: number;
    surfaceOffset: number; // смещение поверхности (от пола, либо от стены)
    rotationX: number;
    rotationY: number;
    rotationZ: number;
}

interface ChangeFunctionInterface {
    name: keyof PropertyInterface;
    value: number;
}

const getMaxSurfaceOffset = (
    decor: DecorItemI,
    roomSizes: {
        roomLength: number;
        roomHeight: number;
        roomDepth: number;
    },
    property: PropertyInterface
) => {
    const isOnDepth = decor.side === "back" || decor.side === "front";

    return isOnDepth ? roomSizes.roomDepth - property.depth : roomSizes.roomLength - property.depth;
};

const EditDecorModal = () => {
    const { currentDecorId, selectDecor, currentSide } = useStoreSystem(
        (state) => ({
            currentDecorId: state.currentDecorId,
            selectDecor: state.selectDecor,
            currentSide: state.currentSide,
        }),
        shallow
    );

    const { updateDecor, decors, removeDecor } = useStoreRoom(
        (state) => ({
            updateDecor: state.updateDecor,
            decors: state.decors,
            removeDecor: state.removeDecor,
        }),
        shallow
    );

    const roomSizes = useStoreRoom(
        (state) => ({
            roomLength: state.length,
            roomHeight: state.height,
            roomDepth: state.depth,
        }),
        shallow
    );

    const currentDecor = decors.find((decor) => decor.id === currentDecorId) || null;

    // Отладочные логи для диагностики
    if (currentDecor) {
        // console.log("🔍 Текущий декор:", {
        //     name: currentDecor.name,
        //     type: currentDecor.type,
        //     surface: currentDecor.surface,
        //     isTriangular: isTriangularPrism(currentDecor)
        // });
    }

    let wallWidth = 0;
    const wallHeight =  roomSizes.roomHeight;
    if (
        currentSide === "front" ||
        currentSide === "back" ||
        currentSide === "top" ||
        currentDecor?.surface === "floor" ||
        currentDecor?.surface === "ceiling"
    ) {
        wallWidth = roomSizes.roomLength;
    }
    if ((currentSide === "left" || currentSide === "right") && currentDecor?.surface === "wall") {
        wallWidth = roomSizes.roomDepth;
    }

    const paddingsPayload = currentDecor ? calcRelativePaddings(currentDecor, roomSizes) : null;

    const [property, setProperty] = useState<PropertyInterface>({
        width: currentDecor?.width ? Math.round(currentDecor.width * 1000) : 0,
        height: currentDecor?.height ? Math.round(currentDecor.height * 1000) : 0,
        depth: currentDecor?.depth ? Math.round(currentDecor.depth * 1000) : 0,
        margin: currentDecor?.margin ? Math.round(currentDecor.margin) : 0,
        paddingLeft: paddingsPayload ? paddingsPayload?.left : 0,
        paddingRight: paddingsPayload ? paddingsPayload?.right : 0,
        paddingTop: paddingsPayload ? paddingsPayload?.top : 0,
        paddingBottom: paddingsPayload ? paddingsPayload?.bottom : 0,
        surfaceOffset: currentDecor?.surfaceOffset ? Math.round(currentDecor.surfaceOffset) : 0,
        rotationX: currentDecor?.rotation.x ? currentDecor.rotation.x : 0,
        rotationY: currentDecor?.rotation.y ? currentDecor.rotation.y : 0,
        rotationZ: currentDecor?.rotation.z ? currentDecor.rotation.z : 0,
    });

    const setPropertyObject = ({ name, value }: ChangeFunctionInterface) => {
        const newProperty: PropertyInterface = structuredClone(property);
        if (name in property) {
            const newName = name as keyof PropertyInterface;
            newProperty[newName] = value;

            if (name === "width") {
                const difference = (property.width - value) / 2;
                if (newProperty.paddingLeft + difference >= property.margin) {
                    newProperty.paddingLeft += difference;
                } else {
                    newProperty.paddingRight += difference;
                }
                if (newProperty.paddingRight + difference >= property.margin) {
                    newProperty.paddingRight += difference;
                } else {
                    newProperty.paddingLeft += difference;
                }
            }
            if (name === "height") {
                const difference = (property.height - value) / 2;
                if (newProperty.paddingTop + difference >= property.margin) {
                    newProperty.paddingTop += difference;
                } else {
                    newProperty.paddingBottom += difference;
                }
                if (newProperty.paddingBottom + difference >= property.margin) {
                    newProperty.paddingBottom += difference;
                } else {
                    newProperty.paddingTop += difference;
                }
            }
            if (name === "margin") {
                if (property.paddingLeft < value) {
                    const difference = value - property.paddingLeft;
                    newProperty.paddingLeft = value;
                    newProperty.paddingRight -= difference;
                }
                if (property.paddingRight < value) {
                    const difference = value - property.paddingRight;
                    newProperty.paddingRight = value;
                    newProperty.paddingLeft -= difference;
                }
                if (property.paddingTop < value) {
                    const difference = value - property.paddingTop;
                    newProperty.paddingTop = value;
                    newProperty.paddingBottom -= difference;
                }
                if (property.paddingBottom < value) {
                    const difference = value - property.paddingBottom;
                    newProperty.paddingBottom = value;
                    newProperty.paddingTop -= difference;
                }
            }
            if (name === "paddingLeft") {
                newProperty.paddingRight += property.paddingLeft - value;
            }
            if (name === "paddingRight") {
                newProperty.paddingLeft += property.paddingRight - value;
            }
            if (name === "paddingTop") {
                newProperty.paddingBottom += property.paddingTop - value;
            }
            if (name === "paddingBottom") {
                newProperty.paddingTop += property.paddingBottom - value;
            }
            if (name === "surfaceOffset") {
                newProperty.surfaceOffset = value;
            }
            if (name.includes("rotation")) {
                newProperty[name] = degToRad(value);
            }
            setProperty(newProperty);
        }
    };
    if (!currentDecor) return null;

    // const availablePaddings = calcAvailablePaddings(currentDecor, roomSizes, currentSide); //FIXME такое чувство что она не правильно подбирает падинги...

    const getMaxMargin = () => {
        const maxHorizontalMargin = wallWidth - property.width;
        const maxVerticalMargin = wallHeight - property.height;
        return Math.floor(maxHorizontalMargin < maxVerticalMargin ? maxHorizontalMargin / 2 : maxVerticalMargin / 2);
    };

    const resetProperty = () => {
        setProperty({
            width: currentDecor?.width ? Math.round(currentDecor.width * 1000) : 0,
            height: currentDecor?.height ? Math.round(currentDecor.height * 1000) : 0,
            depth: currentDecor?.depth ? Math.round(currentDecor.depth * 1000) : 0,
            margin: currentDecor?.margin ? Math.round(currentDecor.margin) : 0,
            paddingLeft: paddingsPayload ? paddingsPayload?.left : 0,
            paddingRight: paddingsPayload ? paddingsPayload?.right : 0,
            paddingTop: paddingsPayload ? paddingsPayload?.top : 0,
            paddingBottom: paddingsPayload ? paddingsPayload?.bottom : 0,
            surfaceOffset: currentDecor?.surfaceOffset ? Math.round(currentDecor.surfaceOffset) : 0,
            rotationX: currentDecor?.rotation.x ? currentDecor.rotation.x : 0,
            rotationY: currentDecor?.rotation.y ? currentDecor.rotation.y : 0,
            rotationZ: currentDecor?.rotation.z ? currentDecor.rotation.z : 0,
        });
    };

    const acceptProperty = () => {
        const position: [number, number, number] = [...currentDecor.position];
        const translate: [number, number, number] = [...currentDecor.translate];

        if (["floor", "ceiling"].includes(currentDecor.surface)) {
            position[0] = (-wallWidth + property.width) / 2000 + property.paddingLeft / 1000;
            position[2] = (property.depth / 2 + property.paddingBottom) / 1000;
        } else {
            position[1] = (property.paddingBottom + property.height / 2) / 1000;
        }

        if (currentDecor.type !== 'constructions') {
            translate[1] = property.height / 2000;
        }

        if (currentDecor.surface === "wall") {
            if (currentSide === "front") {
                position[0] = (-wallWidth + property.width) / 2000 + property.paddingLeft / 1000;
            }
            if (currentSide === "back") {
                position[0] = (-wallWidth + property.width) / 2000 + property.paddingRight / 1000;
            }
            if (currentSide === "left") {
                position[2] = (wallWidth - property.width / 2 - property.paddingLeft) / 1000;
            }
            if (currentSide === "right") {
                position[2] = (property.width / 2 + property.paddingLeft) / 1000;
            }
        }

        updateDecor({
            ...currentDecor,
            position,
            translate,
            width: property.width / 1000,
            height: property.height / 1000,
            depth: property.depth / 1000,
            margin: property.margin,
            surfaceOffset: property.surfaceOffset,
            rotation: new Vector3(property.rotationX, property.rotationY, property.rotationZ), //TODO перевод вв радианы???
        });
        selectDecor(null);
    };

    const deleteDecor = () => {
        currentDecorId && removeDecor(currentDecorId);
        selectDecor(null);
    };

    return (
        <CustomModal
            isOpenModal={currentDecorId !== null}
            onCloseModalCallback={() => {
                selectDecor(null);
            }}
        >
            <>
                <div className={styles.title}>{currentDecor.name}</div>
                <div>
                    <div className={styles.blockName}>
                        <ObjectSize />
                        <div className={styles.group}>Размер объекта</div>
                    </div>
                    <div className={styles.blockSizes}>
                        <SizeBlock
                            icon={
                                <div className={styles.propsIcon}>
                                    <LinearSize />
                                </div>
                            }
                            value={property.width}
                            step={10}
                            max={wallWidth - property.margin * 2}
                            name="width"
                            changeFunction={setPropertyObject}
                        />
                        <SizeBlock
                            icon={
                                <div className={styles.propsIcon}>
                                    <VerticalSize />
                                </div>
                            }
                            value={property.height}
                            step={10}
                            max={wallHeight - property.margin * 2}
                            name="height"
                            changeFunction={setPropertyObject}
                        />
                        <SizeBlock
                            icon={
                                <div className={styles.propsIcon}>
                                    <DepthSize />
                                </div>
                            }
                            value={property.depth}
                            step={10}
                            name="depth"
                            changeFunction={setPropertyObject}
                        />
                        {currentDecor.type !== "constructions" && (
                            <SizeBlock
                                icon={
                                    <div className={styles.propsIcon}>
                                        <MarginBoxSize />
                                    </div>
                                }
                                value={property.margin}
                                step={10}
                                min={0}
                                max={getMaxMargin()}
                                name="margin"
                                changeFunction={setPropertyObject}
                            />
                        )}
                        {["floor", "ceiling"].includes(currentDecor.surface) && (
                            <SizeBlock
                                icon={
                                    <div className={styles.propsIcon}>
                                        <MarginUp />
                                    </div>
                                }
                                value={property.surfaceOffset}
                                step={100}
                                min={0}
                                max={wallHeight - property.height}
                                name="surfaceOffset"
                                changeFunction={setPropertyObject}
                            />
                        )}
                        {currentDecor.surface === "wall" && (
                            <SizeBlock
                                icon={
                                    <div className={styles.propsIcon}>
                                        <MarginForward />
                                    </div>
                                }
                                value={property.surfaceOffset}
                                step={100}
                                min={0}
                                max={getMaxSurfaceOffset(currentDecor, roomSizes, property)}
                                name="surfaceOffset"
                                changeFunction={setPropertyObject}
                            />
                        )}
                    </div>

                    {currentDecor.type === "constructions" && currentDecor.code === "vnutrennyaya-stena" && (
                        <div>
                            <div className={styles.blockName}>
                                <LinearSize />
                                <div className={styles.group}>Функции конструкции</div>
                            </div>
                            <div className={styles.blockSizes}>
                                <button
                                    className={styles.stretchButton}
                                    onClick={() => {
                                        // console.log("🔧 Нажата кнопка 'Растянуть до стен'");

                                        const { newWidth, newPosition, debugVisualization } = calculateStretchToWallsAdvanced(
                                            currentDecor,
                                            {
                                                length: roomSizes.roomLength,
                                                depth: roomSizes.roomDepth,
                                                height: roomSizes.roomHeight
                                      
                                            }
                                        );

                                        if (debugVisualization) {
                                            // console.log("🎨 3D визуализация лучей:", debugVisualization);
                                            
                                        }

                                        // Обновляем ширину
                                        setPropertyObject({ name: "width", value: newWidth });

                                        // Обновляем позицию декора
                                        const updatedDecor = {
                                            ...currentDecor,
                                            width: newWidth / 1000, // Конвертируем из мм в метры
                                            position: newPosition
                                        };
                                        updateDecor(updatedDecor);

                                        // console.log("✅ Стена растянута и обновлена");
                                    }}
                                >
                                    Растянуть до стен
                                </button>
                            </div>
                        </div>
                    )}

                    {["floor", "ceiling"].includes(currentDecor.surface) && (
                        <div>
                            <div className={styles.blockName}>
                                <RotationOfObject width={24} height={24} />
                                <div className={styles.group}>Вращение объекта</div>
                            </div>
                            <div className={styles.blockSizes}>
                                <SizeBlock
                                    icon={
                                        <div className={styles.propsIcon}>
                                            <RotateXAxis />
                                        </div>
                                    }
                                    value={Math.round(radToDeg(property.rotationX))}
                                    step={10}
                                    min={0}
                                    max={360}
                                    name="rotationX"
                                    changeFunction={setPropertyObject}
                                />
                                <SizeBlock
                                    icon={
                                        <div className={styles.propsIcon}>
                                            <RotateYAxis />
                                        </div>
                                    }
                                    value={Math.round(radToDeg(property.rotationY))}
                                    step={10}
                                    min={0}
                                    max={360}
                                    name="rotationY"
                                    changeFunction={setPropertyObject}
                                />
                                <SizeBlock
                                    icon={
                                        <div className={styles.propsIcon}>
                                            <RotateZAxis />
                                        </div>
                                    }
                                    value={Math.round(radToDeg(property.rotationZ))}
                                    step={10}
                                    min={0}
                                    max={360}
                                    name="rotationZ"
                                    changeFunction={setPropertyObject}
                                />
                            </div>
                        </div>
                    )}

                    {[ "ceiling"].includes(currentDecor.surface) && (() => {
                        // console.log("🏠 Секция функций перекрытия отображается для:", currentDecor.name);
                        return true;
                    })() && (
                        <div>
                            <div className={styles.blockName}>
                                <LinearSize />
                                <div className={styles.group}>Вид перекрытия</div>
                            </div>
                            <div className={styles.blockSizes}>
                                {(() => {
                                    const isTriangular = isTriangularPrism(currentDecor);
                                    // console.log("🔍 Проверка isTriangularPrism в рендере:", isTriangular);
                                    return !isTriangular;
                                })() ? (
                                    <button
                                        className={styles.stretchButton}
                                        onClick={() => {
                                            // console.log("🔺 Нажата кнопка 'Отсечь половину'");
                                            // console.log("📦 Исходный декор:", currentDecor);

                                            try {
                                                const triangularDecor = createTriangularPrism(currentDecor, 'top-right');
                                                // console.log("🔺 Созданная треугольная призма:", triangularDecor);

                                                updateDecor(triangularDecor);
                                                // console.log("✅ updateDecor вызван");
// 
                                                // Проверяем что декор действительно обновился
                                                setTimeout(() => {
                                                    const storeDecors = useStoreRoom.getState().decors;
                                                    const updatedDecor = storeDecors.find(d => d.id === currentDecor.id);
                                                    // console.log("🔄 Декор после обновления из store:", updatedDecor);
                                                    // console.log("🔍 Полное triangularPrism свойство:", JSON.stringify((updatedDecor as any)?.triangularPrism, null, 2));
                                                }, 100);

                                            } catch (error) {
                                                // console.error("❌ Ошибка при создании треугольной призмы:", error);
                                            }
                                        }}
                                    >
                                        Отсечь половину
                                    </button>
                                ) : (
                                    <div className={styles.blockSizes}>
                                        <button
                                            className={styles.stretchButton}
                                            onClick={() => {
                                                // console.log("🔄 Изменение направления треугольной призмы");

                                                const currentDirection = currentDecor.triangularPrism?.triangleDirection;
                                                const newDirection = cycleTriangularDirection(currentDirection);
                                                const updatedDecor = createTriangularPrism(currentDecor, newDirection);
                                                updateDecor(updatedDecor);

                                                // console.log(`✅ Направление изменено на: ${newDirection}`);
                                            }}
                                        >
                                            Изменить направление
                                        </button>
                                        <button
                                            className={clsx(styles.stretchButton, styles.resetButton)}
                                            onClick={() => {
                                                // console.log("🔄 Сброс треугольной призмы");

                                                const resetDecor = resetTriangularPrism(currentDecor);
                                                updateDecor(resetDecor);

                                                // console.log("✅ Треугольная призма сброшена");
                                            }}
                                        >
                                            Сбросить
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div>
                        <div className={styles.blockName}>
                            <LocationOfObject />
                            <div className={styles.group}>Расположение объекта</div>
                        </div>
                        <div className={styles.blockSizes}>
                            <SizeBlock
                                icon={
                                    <div className={clsx(styles.propsIcon)}>
                                        <SizeFromLeft />
                                    </div>
                                }
                                value={property.paddingLeft}
                                step={100}
                                min={property.margin}
                                max={wallWidth - property.width - property.margin}
                                name="paddingLeft"
                                changeFunction={setPropertyObject}
                            />
                            <SizeBlock
                                icon={
                                    <div className={clsx(styles.propsIcon, styles.propsIcon_plus90)}>
                                        <SizeFromLeft />
                                    </div>
                                }
                                value={property.paddingTop}
                                step={100}
                                min={property.margin}
                                max={
                                    wallHeight -
                                    (currentDecor.surface === "wall" ? property.height : property.depth) -
                                    property.margin
                                }
                                name="paddingTop"
                                changeFunction={setPropertyObject}
                            />
                            <SizeBlock
                                icon={
                                    <div className={clsx(styles.propsIcon, styles.propsIcon_plus180)}>
                                        <SizeFromLeft />
                                    </div>
                                }
                                value={property.paddingRight}
                                step={100}
                                min={property.margin}
                                max={wallWidth - property.width - property.margin}
                                name="paddingRight"
                                changeFunction={setPropertyObject}
                            />
                            <SizeBlock
                                icon={
                                    <div className={clsx(styles.propsIcon, styles.propsIcon_minus90)}>
                                        <SizeFromLeft />
                                    </div>
                                }
                                value={property.paddingBottom}
                                step={100}
                                min={property.margin}
                                max={
                                    wallHeight -
                                    (currentDecor.surface === "wall" ? property.height : property.depth) -
                                    property.margin
                                }
                                name="paddingBottom"
                                changeFunction={setPropertyObject}
                            />
                        </div>
                    </div>
                </div>
                <div className={styles.buttonBlock_main}>
                    <div className={styles.buttonBlock_left}>
                        <button
                            className={clsx(styles.buttonBlock_button, styles.buttonBlock_button__secondary)}
                            onClick={resetProperty}
                        >
                            Сброс
                            <ResetForButton />
                        </button>
                        {/* кнопка копирования */}
                        <button
                            className={clsx(styles.buttonBlock_button, styles.buttonBlock_button__primary)}
                            onClick={deleteDecor}
                        >
                            <Trashcan />
                        </button>
                    </div>
                    <div className={styles.buttonBlock_right}>
                        <button
                            className={clsx(styles.buttonBlock_button, styles.buttonBlock_button__accept)}
                            onClick={acceptProperty}
                        >
                            Принять
                        </button>
                    </div>
                </div>
            </>
        </CustomModal>
    );
};

export default EditDecorModal;
